// @ts-nocheck
import { Box } from '@mui/material';
import {
  Notebook,
  MapPin,
  Info,
  Clock,
  CheckCircle
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Editor from '../../Editor/Editor';
import {
  formatDateForDisplay,
  formatDurationDisplay,
  formatTimeLocale
} from '../../../utils/dateUtility';
import { getLineItemServiceType } from '../../../constants/lineItemServiceTypes';
import {
  getLineItemImageUrl,
  getLineItemAttachmentUrl
} from '../../../utils/resourcePathHelper';
import { imageSize } from '../../../constants/files';
import useComponentHeight from '../../../hooks/useComponentHeight';
import IconSection from '../../Trip/DetailedItinerary/TravelDoc/ServiceSection/Components/IconSection';
import { ensureHttps } from '../../../utils/urlHelper';
import { sortMealOptions } from '../../../constants/lineItemFieldConfig';
import * as P from '@phosphor-icons/react';
import './TravelDocLineItemDisplay.css';

/**
 * TravelDocLineItemDisplay - Display component for LineItems in Travel Docs PDF
 * Copied from TDDS Service components and adapted to use LineItem data
 */
const TravelDocLineItemDisplay = ({ lineItem, tenantsettings, icon }) => {
  const { ref, height } = useComponentHeight();
  const { t } = useTranslation(['common', 'services']);

  const {
    lineitemid,
    servicetypecode,
    title,
    description,
    vendorname,
    vendorphone,
    vendorlink,
    address,
    startlocation,
    endlocation,
    starttime,
    endtime,
    durationtime,
    confirmationnumber,
    serviceclass,
    servicenumber,
    mealsincluded,
    contactname,
    startDate,
    endDate,
    enddayoffset,
    lineitemimage = [],
    lineitemattachment = [],
    servicetypeicon
  } = lineItem;

  // Map LineItem fields to service type display names
  const getServiceTypeName = () => {
    const typeNames = {
      HTL: t('services:accommodation', 'Accommodation'),
      FLT: t('services:flight', 'Flight'),
      ACT: t('services:activity', 'Activity'),
      RST: t('services:restaurant', 'Restaurant'),
      TRF: t('services:transfer', 'Transfer'),
      CAR: t('services:carRental', 'Car Rental'),
      TRN: t('services:train', 'Train'),
      INF: t('services:info', 'Info')
    };
    return typeNames[servicetypecode] || t('services:service', 'Service');
  };

  const getServiceTypeIcon = () => {
    const mapped = getLineItemServiceType(servicetypecode);
    const iconref = mapped?.iconref;
    const ServiceTypeIcon =
      iconref && typeof P[iconref] === 'object' ? P[iconref] : null;
    return ServiceTypeIcon;
  };

  const ServiceTypeIcon = getServiceTypeIcon();

  // Helper function to check if a value has meaningful content (not just whitespace)
  const hasValue = (value) => {
    return value && typeof value === 'string' && value.trim() !== '';
  };

  // Build image URLs
  const imageUrls = lineitemimage
    .sort((a, b) => a.seq - b.seq)
    .map((img) => {
      if (img.resourcepath) {
        return getLineItemImageUrl({
          resourcePath: img.resourcepath,
          sizePrefix: imageSize.large.prefix
        });
      }
      return null;
    })
    .filter(Boolean);

  // Build attachment links
  const attachmentLinks = lineitemattachment
    .sort((a, b) => a.seq - b.seq)
    .map((att) => {
      if (att.resourcepath) {
        const url = getLineItemAttachmentUrl({
          resourcePath: att.resourcepath
        });
        return { name: att.attachmentname || 'Attachment', url };
      }
      return null;
    })
    .filter(Boolean);

  // Parse meals included
  const mealsArray = mealsincluded ? sortMealOptions(mealsincluded) : [];

  // Check if we have any column field data to display
  const hasColumnFieldData =
    hasValue(vendorname) ||
    hasValue(vendorphone) ||
    hasValue(vendorlink) ||
    hasValue(address) ||
    hasValue(startlocation) ||
    hasValue(endlocation) ||
    hasValue(starttime) ||
    hasValue(endtime) ||
    hasValue(durationtime) ||
    hasValue(serviceclass) ||
    hasValue(servicenumber) ||
    hasValue(contactname) ||
    startDate ||
    endDate ||
    enddayoffset;

  // Cap at 5 per spec — "max 5 shown"
  const capped = imageUrls?.length > 0 ? imageUrls.slice(0, 5) : imageUrls;

  // Render columns based on service type
  const renderColumns = () => {
    switch (servicetypecode) {
      case 'HTL':
        return (
          <>
            {startDate && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Clock />
                </Box>
                <Box>
                  <p className='service-section__label-value'>
                    <span>{t('services:checkInDate')}:</span>{' '}
                    {formatDateForDisplay(startDate)}
                    {starttime && ', ' + `${formatTimeLocale(starttime)}`}
                  </p>
                  {endDate && (
                    <p className='service-section__label-value'>
                      <span>{t('services:checkOutDate')}:</span>{' '}
                      {formatDateForDisplay(endDate)}
                      {endtime && ', ' + formatTimeLocale(endtime)}
                    </p>
                  )}
                  {enddayoffset ? (
                    <p className='service-section__label-value'>
                      <span>
                        {t('services:numberOfNights', 'Number of Nights')}:
                      </span>{' '}
                      {enddayoffset}
                    </p>
                  ) : null}
                </Box>
              </Box>
            )}

            {(hasValue(vendorname) || hasValue(vendorphone)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Notebook />
                </Box>
                <Box>
                  {hasValue(vendorname) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:hotelName')}:</span> {vendorname}
                    </p>
                  )}
                  {hasValue(vendorphone) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:contactTel')}:</span> {vendorphone}
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {hasValue(address) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <MapPin />
                </Box>
                <Box>
                  <p className='service-section__label-value'>
                    <span>{t('services:address')}:</span> {address}
                  </p>
                </Box>
              </Box>
            )}

            {(hasValue(serviceclass) || hasValue(vendorlink)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Info />
                </Box>
                <Box>
                  {hasValue(serviceclass) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:roomType')}:</span> {serviceclass}
                    </p>
                  )}
                  {hasValue(vendorlink) && (
                    <p className='service-section__label-value'>
                      <a
                        href={ensureHttps(vendorlink)}
                        rel='noreferrer'
                        target='_blank'
                      >
                        {t('services:website')}
                      </a>
                    </p>
                  )}
                </Box>
              </Box>
            )}
          </>
        );

      case 'FLT':
        return (
          <>
            {(hasValue(vendorname) || hasValue(servicenumber)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Info />
                </Box>
                <Box>
                  {hasValue(vendorname) && (
                    <p className='service-section__label-value'>
                      <span>{t('Airline')}:</span> {vendorname}
                    </p>
                  )}
                  {hasValue(servicenumber) && (
                    <p className='service-section__label-value'>
                      <span>{t('Flight Number')}:</span> {servicenumber}
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {(hasValue(startlocation) || hasValue(endlocation)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <MapPin />
                </Box>
                <Box>
                  {hasValue(startlocation) && (
                    <p className='service-section__label-value'>
                      <span>{t('Leaving From')}:</span> {startlocation}
                    </p>
                  )}
                  {hasValue(endlocation) && (
                    <p className='service-section__label-value'>
                      <span>{t('Arriving To')}:</span> {endlocation}
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {(hasValue(starttime) || hasValue(endtime)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Clock />
                </Box>
                <Box>
                  {hasValue(starttime) && (
                    <p className='service-section__label-value'>
                      <span>{t('Departure Time')}:</span>{' '}
                      {formatTimeLocale(starttime)}
                    </p>
                  )}
                  {hasValue(endtime) && (
                    <p className='service-section__label-value'>
                      <span>{t('Arrival Time')}:</span>{' '}
                      {formatTimeLocale(endtime)}
                      {enddayoffset == 1 && (
                        <span style={{ fontWeight: '300' }}>
                          {' '}
                          ({t('services:nextDayArrival', 'Next Day')})
                        </span>
                      )}
                    </p>
                  )}
                </Box>
              </Box>
            )}
          </>
        );

      case 'ACT':
        return (
          <>
            {(hasValue(vendorname) || hasValue(vendorphone)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Notebook />
                </Box>
                <Box>
                  {hasValue(vendorname) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:provider')}:</span> {vendorname}
                    </p>
                  )}
                  {hasValue(vendorphone) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:contactTel')}:</span> {vendorphone}
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {(hasValue(startlocation) || hasValue(vendorlink)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <MapPin />
                </Box>
                <Box>
                  {hasValue(startlocation) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:address')}:</span> {startlocation}
                    </p>
                  )}
                  {hasValue(vendorlink) && (
                    <p className='service-section__label-value'>
                      <a
                        href={ensureHttps(vendorlink)}
                        rel='noreferrer'
                        target='_blank'
                      >
                        {t('services:website')}
                      </a>
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {(hasValue(starttime) || hasValue(durationtime)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Clock />
                </Box>
                <Box>
                  {hasValue(starttime) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:time')}:</span>{' '}
                      {formatTimeLocale(starttime)}
                    </p>
                  )}
                  {hasValue(durationtime) && (
                    <p className='service-section__label-value'>
                      <span>{t('Duration')}:</span>{' '}
                      {formatDurationDisplay(durationtime)}
                    </p>
                  )}
                </Box>
              </Box>
            )}
          </>
        );

      case 'RST':
        return (
          <>
            {(hasValue(vendorname) || hasValue(vendorphone)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Notebook />
                </Box>
                <Box>
                  {hasValue(vendorname) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:restaurantName')}:</span> {vendorname}
                    </p>
                  )}
                  {hasValue(vendorphone) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:contactTel')}:</span> {vendorphone}
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {(hasValue(address) || hasValue(vendorlink)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <MapPin />
                </Box>
                <Box>
                  {hasValue(address) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:address')}:</span> {address}
                    </p>
                  )}
                  {hasValue(vendorlink) && (
                    <p className='service-section__label-value'>
                      <a
                        href={ensureHttps(vendorlink)}
                        rel='noreferrer'
                        target='_blank'
                      >
                        {t('services:website')}
                      </a>
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {hasValue(starttime) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Clock />
                </Box>
                <Box>
                  <p className='service-section__label-value'>
                    <span>
                      {t('services:reservationTime', 'Reservation Time')}:
                    </span>{' '}
                    {formatTimeLocale(starttime)}
                  </p>
                </Box>
              </Box>
            )}
          </>
        );

      case 'TRF':
        return (
          <>
            {hasValue(vendorname) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Notebook />
                </Box>
                <Box>
                  <p className='service-section__label-value'>
                    <span>{t('services:provider')}:</span> {vendorname}
                  </p>
                </Box>
              </Box>
            )}

            {(hasValue(startlocation) || hasValue(endlocation)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <MapPin />
                </Box>
                <Box>
                  {hasValue(startlocation) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:pickUpLocation')}:</span>{' '}
                      {startlocation}
                    </p>
                  )}
                  {hasValue(endlocation) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:dropOffLocation')}:</span>{' '}
                      {endlocation}
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {(hasValue(starttime) || hasValue(contactname)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Clock />
                </Box>
                <Box>
                  {hasValue(starttime) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:pickUpTime')}:</span>{' '}
                      {formatTimeLocale(starttime)}
                    </p>
                  )}
                  {hasValue(contactname) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:driverName')}:</span> {contactname}
                    </p>
                  )}
                </Box>
              </Box>
            )}
          </>
        );

      case 'CAR':
        return (
          <>
            {(hasValue(vendorname) || hasValue(serviceclass)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Notebook />
                </Box>
                <Box>
                  {hasValue(vendorname) && (
                    <p className='service-section__label-value'>
                      <span>{t('Company')}:</span> {vendorname}
                    </p>
                  )}
                  {hasValue(serviceclass) && (
                    <p className='service-section__label-value'>
                      <span>{t('Car Class')}:</span> {serviceclass}
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {(hasValue(startlocation) || hasValue(endlocation)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <MapPin />
                </Box>
                <Box>
                  {hasValue(startlocation) && (
                    <p className='service-section__label-value'>
                      <span>{t('Pick Up Location')}:</span> {startlocation}
                    </p>
                  )}
                  {hasValue(endlocation) && (
                    <p className='service-section__label-value'>
                      <span>{t('Drop Off Location')}:</span> {endlocation}
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {(startDate || endDate) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Clock />
                </Box>
                <Box>
                  {startDate && (
                    <p className='service-section__label-value'>
                      <span>{t('services:pickUpDate')}:</span>{' '}
                      {formatDateForDisplay(startDate)}{' '}
                      {starttime && formatTimeLocale(starttime)}
                    </p>
                  )}
                  {endDate && (
                    <p className='service-section__label-value'>
                      <span>{t('services:dropOffDate')}:</span>{' '}
                      {formatDateForDisplay(endDate)}{' '}
                      {endtime && formatTimeLocale(endtime)}
                    </p>
                  )}
                </Box>
              </Box>
            )}
          </>
        );

      case 'TRN':
        return (
          <>
            {(hasValue(vendorname) || hasValue(servicenumber)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Info />
                </Box>
                <Box>
                  {hasValue(vendorname) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:trainCompany')}:</span> {vendorname}
                    </p>
                  )}
                  {hasValue(servicenumber) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:trainNumber')}:</span> {servicenumber}
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {(hasValue(startlocation) || hasValue(endlocation)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <MapPin />
                </Box>
                <Box>
                  {hasValue(startlocation) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:departureStation')}:</span>{' '}
                      {startlocation}
                    </p>
                  )}
                  {hasValue(endlocation) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:arrivalStation')}:</span> {endlocation}
                    </p>
                  )}
                </Box>
              </Box>
            )}

            {(hasValue(starttime) || hasValue(endtime)) && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Clock />
                </Box>
                <Box>
                  {hasValue(starttime) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:departureTime')}:</span>{' '}
                      {formatTimeLocale(starttime)}
                    </p>
                  )}
                  {hasValue(endtime) && (
                    <p className='service-section__label-value'>
                      <span>{t('services:arrivalTime')}:</span>{' '}
                      {formatTimeLocale(endtime)}
                      {enddayoffset == 1 && (
                        <span style={{ fontWeight: '300' }}>
                          {' '}
                          ({t('services:nextDayArrival', 'Next Day')})
                        </span>
                      )}
                    </p>
                  )}
                </Box>
              </Box>
            )}
          </>
        );

      case 'INF':
        return (
          <>
            {vendorlink && (
              <Box className='service-section___column'>
                <Box className='service-section___column-info__icon'>
                  <Info />
                </Box>
                <Box>
                  <p className='service-section__label-value'>
                    <a
                      href={ensureHttps(vendorlink)}
                      rel='noreferrer'
                      target='_blank'
                    >
                      {t('services:link', 'Link')}
                    </a>
                  </p>
                </Box>
              </Box>
            )}
          </>
        );

      default:
        return null;
    }
  };

  const hasDescriptionNotes =
    description ||
    confirmationnumber ||
    attachmentLinks.length > 0 ||
    mealsArray.length > 0;

  return (
    <Box
      className='service-section'
      id={`service-section-lineitem-${lineitemid}`}
      fontFamily={tenantsettings?.bodyfont || 'inherit'}
      ref={ref}
    >
      <Box className='service-section__icon-name'>
        <IconSection
          icon={icon || <ServiceTypeIcon />}
          height={height}
          customClass={'service-section__icon-' + servicetypeicon}
        />
        <h3
          className='service-section__name'
          style={{ fontFamily: tenantsettings?.headingfont || 'inherit' }}
        >
          {getServiceTypeName()}
        </h3>
      </Box>

      <Box className='service-section__content'>
        {title && (
          <h5
            className='service-section__title'
            style={{ fontFamily: tenantsettings?.headingfont || 'inherit' }}
          >
            {title}
          </h5>
        )}

        <Box className='service-section___column-info'>{renderColumns()}</Box>

        {hasDescriptionNotes && (
          <Box
            className={`service-section__description-notes ${hasColumnFieldData ? 'spacing' : ''}`}
          >
            {description && (
              <Editor
                id={`service-section-lineitem-${lineitemid}-description`}
                content={description}
                editable={false}
                isPreviewing={true}
                className='service-section__description'
                style={{
                  fontFamily: tenantsettings?.bodyfont || 'inherit'
                }}
              />
            )}

            {confirmationnumber && (
              <Box className='service-section__confirmation'>
                <p>
                  <span>{t('services:confirmation')}</span> {confirmationnumber}
                </p>
              </Box>
            )}

            {attachmentLinks.length > 0 && (
              <Box className='service-section__attachments'>
                <p>
                  <span>{t('services:attachments')}:</span>{' '}
                  {attachmentLinks.map((att, idx) => (
                    <span key={idx}>
                      <a
                        href={att.url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {att.name}
                      </a>
                      {idx < attachmentLinks.length - 1 && ', '}
                    </span>
                  ))}
                </p>
              </Box>
            )}

            {mealsArray.length > 0 && (
              <Box className='service-section__include-values'>
                {mealsArray.map((meal, index) => (
                  <Box
                    className='service-section__include-values__item'
                    key={`meals-included-${meal?.toLowerCase()}-${index}`}
                  >
                    <CheckCircle color='rgba(56, 211, 133, 1)' />
                    <p>{meal}</p>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {imageUrls.length > 0 && (
          <Box
            className={`service-section__images--grid ${tenantsettings?.showImagesInPdf === false ? 'noprint' : ''}`}
          >
            {capped.map(
              (image, index) =>
                image && (
                  <div key={image} className='service-section__images-cell'>
                    <img src={image} alt={`Service image ${index + 1}`} />
                  </div>
                )
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

TravelDocLineItemDisplay.propTypes = {
  lineItem: PropTypes.object.isRequired,
  tenantsettings: PropTypes.object,
  icon: PropTypes.node
};

export default TravelDocLineItemDisplay;
